import { ClientLogo } from "@/components/ClientLogo";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { useClientConfig } from "@/contexts/ClientConfigContext";
import { api, type ComandaCozinhaCard } from "@/lib/api";
import { formatAtendimentoAmbos, formatAtendimentoDisplay, type ModoAtendimento, parseAtendimentoIdentificacao } from "@/lib/atendimentoMode";
import { formatDisplayText } from "@/lib/formatDisplayText";
import { Loader2, Timer } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "@/lib/appToast";

const POLL_MS = 2000;
const ALERTA_AUDIO_URL = "/imagens/sound%20comandas.mp3";
const ALERTA_AUDIO_VOLUME = 1;

function formatElapsed(isoStart: string, nowMs: number): string {
  const start = new Date(isoStart).getTime();
  if (Number.isNaN(start)) return "00h00m00s";
  const sec = Math.max(0, Math.floor((nowMs - start) / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${String(h).padStart(2, "0")}h${String(m).padStart(2, "0")}m${String(s).padStart(2, "0")}s`;
}

function ComandaTimer({ iniciadoEm }: { iniciadoEm: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return (
    <span className="comandas-card-timer">
      <Timer className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
      {formatElapsed(iniciadoEm, now)}
    </span>
  );
}

function formatCardHeader(card: ComandaCozinhaCard, modo: ModoAtendimento): string {
  const base = card.mesa || card.nome;
  const parsed = parseAtendimentoIdentificacao(base, modo);
  const clienteNome = card.cliente_nome?.trim() || parsed.nome;
  if (modo === "ambos") return formatAtendimentoAmbos(clienteNome, card.numero_comanda ?? parsed.numero, card.numero_mesa ?? parsed.mesa);

  if (parsed.numero) {
    return formatAtendimentoDisplay(modo, clienteNome, parsed.numero);
  }

  return formatDisplayText(clienteNome || card.nome);
}

function formatQty(qty: number): string {
  const n = Number(qty);
  if (Number.isInteger(n)) return String(n);
  return n.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
}

/** Assinatura operacional completa do card para detectar mudanças reais. */
function comandaCozinhaSignature(card: ComandaCozinhaCard): string {
  const itens = [...card.itens]
    .sort((a, b) => a.id - b.id)
    .map((i) => ({
      id: i.id,
      nome: (i.nome_produto ?? "").trim(),
      quantidade: Number(i.quantidade).toFixed(3),
      observacao: (i.observacao ?? "").trim(),
    }));

  const normalized = {
    comanda_id: card.comanda_id,
    nome: (card.nome ?? "").trim(),
    mesa: (card.mesa ?? "").trim(),
    cliente_nome: (card.cliente_nome ?? "").trim(),
    cliente_whatsapp: (card.cliente_whatsapp ?? "").trim(),
    origem: (card.origem ?? "").trim(),
    iniciado_em: card.iniciado_em,
    itens,
  };

  return JSON.stringify(normalized);
}

export default function ComandasPage() {
  return (
    <RequireAuth
      redirectTo="/kds"
      loginTitle="Faça login para acessar a cozinha"
    >
      <ComandasPainel />
    </RequireAuth>
  );
}

function ComandasPainel() {
  const { config: clientConfig } = useClientConfig();
  const [cards, setCards] = useState<ComandaCozinhaCard[]>([]);
  const [finalizandoId, setFinalizandoId] = useState<number | null>(null);
  const knownComandaStateRef = useRef<Map<number, string> | null>(null);
  const alertAudioRef = useRef<HTMLAudioElement | null>(null);
  const audioUnlockedRef = useRef(false);

  useEffect(() => {
    const audio = new Audio(ALERTA_AUDIO_URL);
    audio.volume = ALERTA_AUDIO_VOLUME;
    audio.preload = "auto";
    audio.load();
    alertAudioRef.current = audio;
    return () => {
      audio.pause();
      alertAudioRef.current = null;
    };
  }, []);

  const unlockAudio = useCallback(async () => {
    if (audioUnlockedRef.current) return;
    const audio = alertAudioRef.current;
    if (!audio) return;
    try {
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      audioUnlockedRef.current = true;
    } catch {
      // Navegador ainda bloqueia até nova interação.
    }
  }, []);

  const playNovaComandaAlerta = useCallback(() => {
    const audio = alertAudioRef.current;
    if (!audio) return;
    if (!audioUnlockedRef.current) {
      console.warn("Som bloqueado pelo navegador até interação do usuário");
      return;
    }
    audio.currentTime = 0;
    void audio.play().catch(() => {
      console.warn("Som bloqueado pelo navegador até interação do usuário");
    });
  }, []);

  const handlePageInteraction = useCallback(() => {
    void unlockAudio();
  }, [unlockAudio]);

  useEffect(() => {
    const onInteraction = () => {
      void unlockAudio();
    };
    window.addEventListener("pointerdown", onInteraction, { capture: true });
    window.addEventListener("keydown", onInteraction, { capture: true });
    return () => {
      window.removeEventListener("pointerdown", onInteraction, { capture: true });
      window.removeEventListener("keydown", onInteraction, { capture: true });
    };
  }, [unlockAudio]);

  const carregar = useCallback(async () => {
    const data = await api.listarComandasCozinha();
    const newCards = data.cards ?? [];
    const nextState = new Map<number, string>();
    for (const card of newCards) {
      nextState.set(card.comanda_id, comandaCozinhaSignature(card));
    }

    const known = knownComandaStateRef.current;
    if (known === null) {
      knownComandaStateRef.current = nextState;
    } else {
      const hasOperationalChange = [...nextState.entries()].some(([id, signature]) => {
        const prev = known.get(id);
        return prev === undefined || prev !== signature;
      });
      if (hasOperationalChange) {
        playNovaComandaAlerta();
      }
      knownComandaStateRef.current = nextState;
    }

    setCards(newCards);
  }, [playNovaComandaAlerta]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        await carregar();
      } catch (e) {
        if (!cancelled) console.error(e);
      }
    };
    void tick();
    const id = window.setInterval(() => void tick(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [carregar]);

  const handleFinalizar = async (card: ComandaCozinhaCard) => {
    const itemIds = card.itens.map((i) => i.id);
    if (itemIds.length === 0) return;
    try {
      setFinalizandoId(card.comanda_id);
      await api.finalizarItensComandaCozinha(itemIds);
      setCards((prev) => prev.filter((c) => c.comanda_id !== card.comanda_id));
      knownComandaStateRef.current?.delete(card.comanda_id);
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : "Erro ao finalizar");
    } finally {
      setFinalizandoId(null);
    }
  };

  const sortedCards = useMemo(
    () =>
      [...cards].sort((a, b) => new Date(a.iniciado_em).getTime() - new Date(b.iniciado_em).getTime()),
    [cards],
  );

  return (
    <div className="comandas-page" onClick={handlePageInteraction} onKeyDown={handlePageInteraction}>
      <ClientLogo variant="background" alt="" className="comandas-watermark" />
      <div className="comandas-grid">
        {sortedCards.map((card) => (
          <article key={card.comanda_id} className="comandas-card">
            <header className="comandas-card-header">
              <div className="comandas-card-header-text">
                <h2 className="comandas-card-title">{formatCardHeader(card, clientConfig.modo_atendimento)}</h2>
              </div>
              <ComandaTimer iniciadoEm={card.iniciado_em} />
            </header>
            <ul className="comandas-card-items">
              {card.itens.map((item, idx) => (
                <li key={item.id} className="comandas-card-item">
                  {idx > 0 ? <div className="comandas-card-divider" aria-hidden /> : null}
                  <div className="comandas-card-item-row">
                    <span className="comandas-qty-badge">{formatQty(item.quantidade)}</span>
                    <span className="comandas-item-name">{formatDisplayText(item.nome_produto)}</span>
                  </div>
                  {item.observacao?.trim() ? (
                    <p className="comandas-item-obs">{formatDisplayText(item.observacao)}</p>
                  ) : null}
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="comandas-btn-finalizado"
              disabled={finalizandoId === card.comanda_id}
              onClick={() => void handleFinalizar(card)}
            >
              {finalizandoId === card.comanda_id ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                "Finalizado"
              )}
            </button>
          </article>
        ))}
      </div>
    </div>
  );
}
